import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import { RELATION_SUGGESTIONS } from '@twinden/shared';
import { ApiError } from '../../lib/api';
import { useMemberLabels } from '../../hooks/useMemberLabels';
import { useT } from '../../i18n';

// "What do you call Ravi?" (docs/indian-touches.md, D143): Dadi, Nani, Bua…
// or anything typed. Only the person setting it sees it.
export function RelationNameDialog({
  person,
  isOpen,
  onClose,
}: {
  person: { id: string; name: string };
  isOpen: boolean;
  onClose: () => void;
}) {
  const { labels, setLabel } = useMemberLabels();
  const current = labels[person.id] ?? '';
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const t = useT();

  useEffect(() => {
    if (isOpen) setValue(current);
  }, [isOpen, current]);

  const save = async (label: string) => {
    setSaving(true);
    try {
      await setLabel(person.id, label.trim());
      onClose();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent mx={4} borderRadius="28px">
        <ModalHeader fontSize="22px">
          {t('account.relation.title', { name: person.name })}
        </ModalHeader>
        <ModalBody>
          <Text color="muted" fontSize="15px" pb={3}>
            {t('account.relation.hint')}
          </Text>
          <Wrap spacing={2} pb={4}>
            {RELATION_SUGGESTIONS.map(([english, hindi]) => (
              <WrapItem key={english}>
                <Button
                  size="sm"
                  h="36px"
                  variant={value === english ? 'solid' : 'outline'}
                  colorScheme={value === english ? 'brand' : 'gray'}
                  borderColor="line"
                  onClick={() => setValue(english)}
                >
                  {english} · {hindi}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
          <Input
            h="48px"
            value={value}
            maxLength={40}
            placeholder={t('account.relation.placeholder')}
            aria-label={t('account.relation.label')}
            onChange={(event) => setValue(event.target.value)}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          {current && (
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={() => void save('')}
              isDisabled={saving}
            >
              {t('account.relation.useName')}
            </Button>
          )}
          <Button
            onClick={() => void save(value)}
            isLoading={saving}
            isDisabled={!value.trim() || value.trim() === current}
          >
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
